import { useEffect, useState } from 'react';
import VideoPlayer from '../components/VideoPlayer';
import AboutSummary from '../components/AboutSummary';
import '../styles/pages/Home.css';

interface HomeProps {
  activatePartyMode: () => void;
}

const Home: React.FC<HomeProps> = ({ activatePartyMode }) => {
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownMessage, setCountdownMessage] = useState('');

  const handleLogoClick = () => {
    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);
    
    if (newCount >= 6) {
      // Start countdown when 5 clicks remain
      setShowCountdown(true);
      
      if (newCount === 6) {
        setCountdownMessage('5 times left');
      } else if (newCount === 7) {
        setCountdownMessage('4 times left');
      } else if (newCount === 8) {
        setCountdownMessage('3 times left');
      } else if (newCount === 9) {
        setCountdownMessage('2 times left');
      } else if (newCount === 10) {
        setCountdownMessage('1 time left');
      } else if (newCount === 11) {
        setCountdownMessage('Party time!');
        setLogoClickCount(0); // Reset counter
        
        // Wait 1 second before activating party mode
        setTimeout(() => {
          activatePartyMode();
          setShowCountdown(false);
          setCountdownMessage('');
        }, 1000);
      }
    }
  };

  useEffect(() => {
    // Set up smooth scrolling for anchor links on this page
    const handleAnchorClick = (e: Event) => {
      const target = e.target as HTMLAnchorElement;
      const href = target.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        
        const targetId = href.substring(1);
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          // Smooth scroll to the target element with offset to not cut off header
          const headerOffset = 85;
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    };

    // Add event listener to the anchor link
    const ctaButton = document.querySelector('a[href="#video"]') as HTMLAnchorElement;
    if (ctaButton) {
      ctaButton.addEventListener('click', handleAnchorClick);
    }

    // Initialize Facebook SDK
    const initializeFacebook = (): void => {
      if (window.FB) {
        window.FB.XFBML.parse();
      } else {
        // Wait for Facebook SDK to load
        setTimeout(initializeFacebook, 100);
      }
    };

    // Initialize Facebook when component mounts
    initializeFacebook();

    // Check if embeds loaded after a delay and show fallbacks if needed
    const checkEmbeds = (): void => {
      setTimeout(() => {
        // Check Facebook embed
        const fbIframe = document.querySelector('.facebook-embed .fb-page iframe');
        const fbFallback = document.querySelector('.facebook-embed .embed-fallback') as HTMLElement;
        if (!fbIframe && fbFallback) {
          fbFallback.style.display = 'block';
        }

        // Check Instagram embed
        const instaIframe = document.querySelector('.instagram-embed iframe');
        const instaFallback = document.querySelector('.instagram-embed .embed-fallback') as HTMLElement;
        if (!instaIframe && instaFallback) {
          instaFallback.style.display = 'block';
        }

        // Twitter embed is now a static content, no need to check for iframe
      }, 5000);
    };

    // Check embeds after component mounts
    checkEmbeds();

    // Cleanup
    return () => {
      if (ctaButton) {
        ctaButton.removeEventListener('click', handleAnchorClick);
      }
    };
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          {showCountdown && (
            <div className={`countdown-message ${countdownMessage === 'Party time!' ? 'party-time' : ''}`}>
              {countdownMessage}
            </div>
          )}
          <img 
            src="/LS_Logo_Text_Only_wht.png" 
            alt="Lost Signal" 
            className="hero-logo-image"
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
          />
          <p className="tagline">A cover band from Albert Lea, Minnesota</p>
          <a href="#video" className="cta-button">Check us out!</a>
        </div>
      </section>

      {/* Video Section */}
      <section id="video" className="video-section">
        <h2 className="section-title">Video Gallery</h2>
        <VideoPlayer />
      </section>

      {/* Social Media Feed Section */}
      <section className="social-media-section">
        <div className="container">
          <h2 className="section-title">Follow Us on Social Media</h2>
          <div className="social-media-grid">
            
            {/* Facebook Embed */}
            <div className="social-embed-container facebook-embed">
              <h3 className="embed-title">Facebook</h3>
              <div className="embed-wrapper">
                <div 
                  className="fb-page" 
                  data-href="https://www.facebook.com/lostsignalmn" 
                  data-tabs="timeline"
                  data-width="400" 
                  data-height="500"
                  data-small-header="false"
                  data-adapt-container-width="true"
                  data-hide-cover="false"
                  data-show-facepile="true"
                >
                  <blockquote 
                    cite="https://www.facebook.com/lostsignalmn" 
                    className="fb-xfbml-parse-ignore"
                  >
                    <a href="https://www.facebook.com/lostsignalmn">Lost Signal</a>
                  </blockquote>
                </div>
                <div className="embed-fallback">
                  <p>Follow us on Facebook!</p>
                  <a 
                    href="https://www.facebook.com/lostsignalmn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="cta-button"
                  >
                    Visit Facebook
                  </a>
                </div>
              </div>
            </div>

            {/* Instagram Embed */}
            <div className="social-embed-container instagram-embed">
              <h3 className="embed-title">Instagram</h3>
              <div className="embed-wrapper">
                <iframe 
                  src="https://www.instagram.com/lostsignalmn/embed"
                  width="400" 
                  height="500"
                  style={{border: 'none', overflow: 'hidden'}}
                  scrolling="no" 
                  frameBorder="0" 
                  allowFullScreen={true}
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  title="Lost Signal Instagram"
                ></iframe>
                <div className="embed-fallback">
                  <p>Follow us on Instagram!</p>
                  <a 
                    href="https://www.instagram.com/lostsignalmn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="cta-button"
                  >
                    Visit Instagram
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="content-section">
        <div className="container">
          <AboutSummary showFullContent={true} />
        </div>
      </section>
    </>
  );
};

export default Home;
